/**
 * Tooltip View Script
 * Handles tooltip positioning and rendering on the frontend
 * using JavaScript to escape overflow:hidden constraints
 */

document.addEventListener('DOMContentLoaded', () => {
  // Add class to body to indicate JS tooltips are active (disables CSS tooltips)
  document.body.classList.add('amp-tooltip-js-enabled');

  let tooltipContainer = null;

  // Create a tooltip container at the body level
  function getTooltipContainer() {
    if (!tooltipContainer) {
      tooltipContainer = document.createElement('div');
      tooltipContainer.className = 'amp-tooltip-portal';
      tooltipContainer.setAttribute('aria-hidden', 'true');
      document.body.appendChild(tooltipContainer);
    }
    return tooltipContainer;
  }

  // Position tooltip relative to trigger element
  function positionTooltip(tooltip, trigger) {
    const triggerRect = trigger.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const isMobile = window.innerWidth <= 768;
    const margin = isMobile ? 10 : 10; // Horizontal margin from viewport edges

    // Hide tooltip if trigger is not in viewport or has no dimensions
    if (triggerRect.width === 0 || triggerRect.height === 0 ||
        triggerRect.top < 0 || triggerRect.bottom > window.innerHeight ||
        triggerRect.left < -triggerRect.width || triggerRect.right > window.innerWidth + triggerRect.width) {
      tooltip.classList.remove('is-visible');
      return;
    }

    // Calculate ideal centered position
    const idealLeft = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);

    // Keep tooltip within viewport with margins
    const minLeft = margin;
    const maxLeft = window.innerWidth - tooltipRect.width - margin;
    const finalLeft = Math.max(minLeft, Math.min(idealLeft, maxLeft));

    // Position above trigger, or below if not enough space above
    const spaceAbove = triggerRect.top;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const gap = 8;

    let finalTop;
    let positionedBelow = false;

    if (spaceAbove >= tooltipRect.height + gap || spaceAbove > spaceBelow) {
      // Position above
      finalTop = triggerRect.top - tooltipRect.height - gap;
    } else {
      // Position below
      finalTop = triggerRect.bottom + gap;
      positionedBelow = true;
    }

    tooltip.style.left = `${finalLeft}px`;
    tooltip.style.top = `${finalTop}px`;

    // Show tooltip if previously hidden
    if (!tooltip.classList.contains('is-visible')) {
      tooltip.classList.add('is-visible');
    }

    // Position the arrow
    const arrow = tooltip.querySelector('.amp-tooltip-portal__arrow');
    if (arrow) {
      const arrowLeft = triggerRect.left + (triggerRect.width / 2) - finalLeft;
      arrow.style.left = `${arrowLeft}px`;

      // Flip arrow if positioned below
      if (positionedBelow) {
        arrow.style.bottom = 'auto';
        arrow.style.top = `-${6}px`; // arrow size
        arrow.style.transform = 'translateX(-50%) rotate(180deg)';
      } else {
        arrow.style.bottom = '-6px';
        arrow.style.top = 'auto';
        arrow.style.transform = 'translateX(-50%)';
      }
    }
  }

  // Show tooltip
  function showTooltip(trigger, content) {
    const container = getTooltipContainer();

    // Create tooltip element
    const tooltip = document.createElement('div');
    tooltip.className = 'amp-tooltip-portal__content';

    // Create text element
    const textDiv = document.createElement('div');
    textDiv.className = 'amp-tooltip-portal__text';
    textDiv.textContent = content; // Use textContent for safety

    // Create arrow element
    const arrowDiv = document.createElement('div');
    arrowDiv.className = 'amp-tooltip-portal__arrow';

    tooltip.appendChild(textDiv);
    tooltip.appendChild(arrowDiv);
    container.appendChild(tooltip);

    // Position tooltip
    requestAnimationFrame(() => {
      positionTooltip(tooltip, trigger);
      tooltip.classList.add('is-visible');
    });

    return tooltip;
  }

  // Hide tooltip
  function hideTooltip(tooltip) {
    if (tooltip && tooltip.parentNode) {
      tooltip.classList.remove('is-visible');
      setTimeout(() => {
        if (tooltip.parentNode) {
          tooltip.parentNode.removeChild(tooltip);
        }
      }, 200); // Match CSS transition duration
    }
  }

  // Handle tooltip triggers
  let currentTooltip = null;
  let hideTimeout = null;

  function handleMouseEnter(event) {
    const trigger = event.currentTarget;
    const content = trigger.getAttribute('data-tooltip');

    if (!content) return;

    // Clear any pending hide
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }

    // Hide current tooltip if different trigger
    if (currentTooltip && currentTooltip.trigger !== trigger) {
      hideTooltip(currentTooltip.element);
      currentTooltip = null;
    }

    // Show new tooltip
    if (!currentTooltip) {
      const tooltipElement = showTooltip(trigger, content);
      currentTooltip = { trigger, element: tooltipElement };
    }
  }

  function handleMouseLeave(event) {
    if (currentTooltip) {
      hideTimeout = setTimeout(() => {
        hideTooltip(currentTooltip.element);
        currentTooltip = null;
      }, 100);
    }
  }

  function handleFocus(event) {
    handleMouseEnter(event);
  }

  function handleBlur(event) {
    handleMouseLeave(event);
  }

  // Reposition on scroll/resize
  function handleReposition() {
    if (currentTooltip) {
      positionTooltip(currentTooltip.element, currentTooltip.trigger);
    }
  }

  // Find scrollable ancestors (handles overflow-x: auto, overflow-y: auto, etc.)
  function findScrollableAncestors(element) {
    const scrollableAncestors = [];
    let parent = element.parentElement;

    while (parent && parent !== document.body) {
      const overflow = window.getComputedStyle(parent).overflow;
      const overflowX = window.getComputedStyle(parent).overflowX;
      const overflowY = window.getComputedStyle(parent).overflowY;

      // Check if element has any scrollable overflow
      if (
        ['auto', 'scroll'].includes(overflow) ||
        ['auto', 'scroll'].includes(overflowX) ||
        ['auto', 'scroll'].includes(overflowY)
      ) {
        scrollableAncestors.push(parent);
      }

      parent = parent.parentElement;
    }

    return scrollableAncestors;
  }

  // Attach event listeners to all tooltip triggers
  function initTooltips() {
    const triggers = document.querySelectorAll('.amp-tooltip[data-tooltip]');

    triggers.forEach(trigger => {
      trigger.addEventListener('mouseenter', handleMouseEnter);
      trigger.addEventListener('mouseleave', handleMouseLeave);
      trigger.addEventListener('focus', handleFocus);
      trigger.addEventListener('blur', handleBlur);

      // Find and attach scroll listeners to scrollable ancestors
      const scrollableAncestors = findScrollableAncestors(trigger);
      scrollableAncestors.forEach(ancestor => {
        // Use a throttled scroll handler to avoid performance issues
        let scrollTimeout;
        ancestor.addEventListener('scroll', () => {
          if (currentTooltip && currentTooltip.trigger === trigger) {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(handleReposition, 10);
          }
        });
      });
    });

    // Reposition on scroll/resize
    let resizeTimeout;
    window.addEventListener('scroll', () => {
      if (currentTooltip) {
        handleReposition();
      }
    }, true); // Use capture to catch scroll on any element

    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleReposition, 100);
    });
  }

  // Initialize
  initTooltips();

  // Re-initialize on dynamic content (for SPAs, AJAX, etc.)
  if (window.MutationObserver) {
    const observer = new MutationObserver((mutations) => {
      let hasNewTooltips = false;
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            if (node.classList && node.classList.contains('amp-tooltip')) {
              hasNewTooltips = true;
            } else if (node.querySelectorAll) {
              const tooltips = node.querySelectorAll('.amp-tooltip[data-tooltip]');
              if (tooltips.length > 0) {
                hasNewTooltips = true;
              }
            }
          }
        });
      });

      if (hasNewTooltips) {
        initTooltips();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
});
